import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getDashConfig, getFormData, getForms, getFormStats, getFormStatskabhi, getGroups, saveDashConfig } from "./apiController";
import { FIELD_TYPES, FieldItem } from "../../widgets/fields";
import { Button, Col, Row } from "reactstrap";
import { Loader } from "../common/FullScreenLoader/fullScreenLoader";
import { elements } from "./elements";
import useFieldItem from "../../widgets/fields/UseFieldItem";
import { dateKeyMapping, idMap, karnatakaBrainHealth, OPERATORS, POS_NEG_FIELD_VALUE } from "./utils"
import DashboardCharts from "./DashboardCharts";
import { CustomButton, showToaster, toasterTypes } from "../../widgets";
import ModalComponent from "../common/Modal/modal";
import { ArrowLeftOutlined, SortAscendingOutlined, SortDescendingOutlined, UnorderedListOutlined } from "@ant-design/icons";


interface InputDataItem {
    [key: string]: number | string;
}

interface TransformedDataItem {
    name: string;
    percentage: number;
    total_data_points: number;
}

interface TransformedData {
    title: string;
    data: TransformedDataItem[];
}

interface InputData {
    [key: string]: {
        [key: string]: number | string;
    }[];
}

interface ValueType {
    value: any;
}

type SortBy = "ascending" | "descending" | "none";

const ProjectDashboard = (props) => {
    const location = useLocation();
    const [loading, setLoading] = useState<boolean>(false);
    const [groupData, setGroupData] = useState<any>();
    const [formsData, setFormsData] = useState<any>([]);
    const [selectedForm, setSelectedForm] = useState<any>();
    const [selectedFormData, setSelectedFormData] = useState<any>();
    const [formSelectedValue, setFormSelectedValue] = useState<any>();
    const [formsDropdownOptions, setFormsDropdownOptions] = useState<any>([]);
    const [noStats, setNoStats] = useState<boolean>(false);
    const [filters, setFilters] = useState<any>({});
    const [filterInfo, setFilterInfo] = useState<any>({});
    const [isUpdatingForm, setIsUpdatingForm] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [initialDashConfig, setinitialDashConfig] = useState(null);
    const [dateScale, setdateScale] = useState<any>({ value: 'month', label: 'Month' });
    const [customFilters, setCustomFilters] = useState<any>({
        dateScale: { value: 'month', label: 'Month' },
        startCreatedDate: null,
        endCreatedDate: null,
    });
    const [sortBy, setSortBy] = useState<SortBy>("none");
    const initialValues = useMemo(() => (props.previousState ?
        props.previousState : {}), [props.previousState]);
    const [values, fields, handleChange, { validateValues, updateValue, resetValue, reset }] =
        useFieldItem(elements, initialValues, { onValueChange });

    const navigate = useNavigate()
    const [projectData, setProjectData] = useState({})
    const [createdDatesError, setCreatedDatesError] = useState<boolean>(false);

    useEffect(() => {
        const temp = localStorage.getItem("coguide_user");
        setProjectData(location?.state)
        getGroupAndForm();
    }, []);

    async function getGroupAndForm() {
        try {
            const groupId = location?.state?.TopicID;
            const groupResponse = await getGroups(groupId);
            setGroupData(groupResponse?.data);

            if (groupResponse.data?.length > 0) {
                const formsResponse = await getForms(groupResponse?.data[0]?.id);

                setFormsData(formsResponse);

                // Create dropdown options after setting formsData
                const dropdownOptions = formsResponse?.map((form) => ({
                    label: form.form_name,
                    value: form.id,
                }));
                setFormsDropdownOptions(dropdownOptions);
            }
        } catch (error) {
            console.error("Error fetching groups or forms:", error);
        }
    }

    function changeForms(value) {
        if (value && value.value) {
            const selected = formsData.find((form) => form.id === value.value);
            setIsUpdatingForm(true);
            setSelectedForm(selected);
            setFormSelectedValue(value);
            reset();
            setCustomFilters({
                dateScale: { value: 'month', label: 'Month' },
                startCreatedDate: null,
                endCreatedDate: null
            })
            fetchFormData(selected?.id);
            setTimeout(() => setIsUpdatingForm(false), 0);
        }
    }

    async function fetchFormData(formId) {
        try {
            setLoading(true);
            let statsRespose;
            if (groupData[0]?.id === karnatakaBrainHealth.projectId && groupData[0]?.topicid === karnatakaBrainHealth.topicId) {
                statsRespose = await getFormStatskabhi(formId);
            } else {
                statsRespose = await getFormStats(formId);
            }
            const dashConfigResponse = await getDashConfig(formId)
            setinitialDashConfig(dashConfigResponse?.data)
            if (!statsRespose?.data || statsRespose?.data.length === 0) {
                setNoStats(true);
                setLoading(false);
                return;
            }
            const filters = statsRespose?.data?.filters;
            const filterinfo = statsRespose?.data?.filter_info;
            const filterElements = createElementsFromFilters(filters, filterinfo);
            setFilters(filterElements);
            setFilterInfo(filterinfo)
            let transformedData: any = []
            if (groupData[0]?.id === karnatakaBrainHealth.projectId && groupData[0]?.topicid === karnatakaBrainHealth.topicId) {
                transformedData = transformDataKabhi(statsRespose?.data);
            } else {
                transformedData = transformData(statsRespose?.data);
            }
            setSelectedFormData(transformedData);
            setNoStats(false);
        } catch (error) {
            setSelectedFormData([])
            console.error("Error fetching form data:", error);
        } finally {
            setLoading(false);
        }
    }

    function isNumber(value: any): value is number {
        return typeof value === 'number' && !isNaN(value);
    }

    // Helper function to format month name
    function formatMonthYear(dateStr: string): string {
        const date = new Date(dateStr);

        // Check if the date is valid
        if (isNaN(date.getTime())) {
            console.warn(`Invalid date string: ${dateStr}`);
            return '';
        }

        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    }

    function transformData(inputData: Record<string, any>): TransformedData[] {
        const { filters, ...dataToTransform } = inputData;
        const result: TransformedData[] = [];

        // Define which date key to use based on scale
        // const dateKeyMapping = {
        //     day: 'DateWise',
        //     month: 'MonthWise',
        //     year: 'YearWise'
        // };

        Object.entries(dataToTransform).forEach(([key, value]) => {
            const scale = customFilters?.dateScale?.value;
            if (Array.isArray(value)) {
                // Handle date data
                if (key.includes(dateKeyMapping[scale])) {
                    const transformedData: TransformedData = {
                        title: key,
                        data: value.map(item => {
                            const valKey = Object.keys(item).find(k => k.endsWith('_VAL')) || '';
                            const perKey = Object.keys(item).find(k => k.endsWith('_PER')) || '';
                            const countKey = Object.keys(item).find(k => k.endsWith('_COUNT')) || '';

                            let displayName = String(item[valKey] || '');

                            // Format the name based on scale
                            if (scale === 'month') {
                                displayName = formatMonthYear(displayName);
                            }

                            return {
                                name: displayName,
                                percentage: isNumber(item[perKey]) ? item[perKey] as number : 0,
                                total_data_points: isNumber(item[countKey]) ? item[countKey] as number : 0
                            };
                        }).sort((a, b) => {
                            // Sort dates in ascending order
                            const dateA = new Date(a.name);
                            const dateB = new Date(b.name);
                            return dateA.getTime() - dateB.getTime();
                        })
                    };
                    result.push(transformedData);
                }
                // Handle non-date data
                else if (Object.values(dateKeyMapping).every(mapping => !key.includes(mapping))) {
                    const transformedData: TransformedData = {
                        title: key,
                        data: value.map(item => {
                            const valKey = Object.keys(item).find(k => k.endsWith('_VAL')) || '';
                            const perKey = Object.keys(item).find(k => k.endsWith('_PER')) || '';
                            const countKey = Object.keys(item).find(k => k.endsWith('_COUNT')) || '';

                            return {
                                name: String(item[valKey] || ''),
                                percentage: isNumber(item[perKey]) ? item[perKey] as number : 0,
                                total_data_points: isNumber(item[countKey]) ? item[countKey] as number : 0
                            };
                        }).sort((a, b) => a.name.toLowerCase().trim().localeCompare(b.name.toLowerCase().trim()))
                    };
                    result.push(transformedData);
                }
            }
        });

        return result;
    }

    function transformDataKabhi(inputData: Record<string, any>): TransformedData[] {
        const { filters, ...dataToTransform } = inputData;
        const result: TransformedData[] = [];

        // Define which date key to use based on scale
        const dateKeyMapping = {
            day: 'DateWise',
            month: 'MonthWise',
            year: 'YearWise'
        };
        const newScale = customFilters?.dateScale?.value;
        Object.entries(dataToTransform).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                // Handle date data
                if (key.includes(dateKeyMapping[newScale])) {
                    const transformedData: TransformedData = {
                        title: key,
                        data: value.map(item => {
                            const valKey = Object.keys(item).find(k => k.endsWith('_VAL')) || '';
                            const perKey = Object.keys(item).find(k => k.endsWith('_PER')) || '';
                            const countKey = Object.keys(item).find(k => k.endsWith('_COUNT')) || '';

                            let displayName = String(item[valKey] || '');

                            // Format the name based on scale
                            if (newScale === 'month') {
                                displayName = formatMonthYear(displayName);
                            }

                            return {
                                name: displayName,
                                percentage: isNumber(item[perKey]) ? item[perKey] as number : 0,
                                total_data_points: isNumber(item[countKey]) ? item[countKey] as number : 0
                            };
                        }).sort((a, b) => {
                            // Sort dates in ascending order
                            const dateA = new Date(a.name);
                            const dateB = new Date(b.name);
                            return dateA.getTime() - dateB.getTime();
                        })
                    };
                    result.push(transformedData);
                }
                // Handle non-date data
                // && (key.toLowerCase().includes("district") || key.toLowerCase().includes("designation"))
                else if (Object.values(dateKeyMapping).every(mapping => !key.includes(mapping))) {
                    const transformedData: TransformedData = {
                        title: key,
                        data: value.map(item => {
                            const valKey = Object.keys(item).find(k => k.endsWith('_VAL')) || '';
                            const perKey = Object.keys(item).find(k => k.endsWith('_PER')) || '';
                            const countKey = Object.keys(item).find(k => k.endsWith('_COUNT')) || '';

                            return {
                                name: String(item[valKey] || ''),
                                percentage: isNumber(item[perKey]) ? item[perKey] as number : 0,
                                total_data_points: isNumber(item[countKey]) ? item[countKey] as number : 0
                            };
                        }).sort((a, b) => a.name.toLowerCase().trim().localeCompare(b.name.toLowerCase().trim()))
                    };
                    result.push(transformedData);
                }
            }
            else if (key.includes(POS_NEG_FIELD_VALUE)) {
                let tot = value['positives'] || 0 + value['negatives'] || 0;
                if (tot <= 0) {
                    return;
                }

                const transformedData: TransformedData = {
                    // title: key,
                    title: "Risk stats for screened people",
                    data: Object.keys(value).map(subKey => {
                        const total = (value['positives'] || 0) + (value['negatives'] || 0);

                        return {
                            name: subKey.charAt(0).toUpperCase() + subKey.slice(1),
                            percentage: total > 0 ? (value[subKey] / total) * 100 : 0,
                            total_data_points: value[subKey]
                        };
                    })
                };
                result.push(transformedData);
            }
        });

        return result;
    }


    function onValueChange(name, value, values, fieldValues = null) {
        let resetValue = {};
        // if (!value || value.value === null) {
        //     return [{
        //         ...values,
        //         [name]: null
        //     }];
        // }
        return [{
            ...values,
            ...resetValue,
            [name]: value,
        }]
    }

    async function updateFilteredData() {

        try {
            setLoading(true);
            const reqObj: any = [];

            Object.entries(values).forEach(([key, value]) => {
                const filter = filters[key];

                if (filter) {
                    if (key.endsWith("_start_date") && filter.appendedStart) {
                        let newKey = key.replace("_start_date", "")
                        if (value) {
                            reqObj.push({
                                key: newKey,
                                operator: "GreaterEq",
                                value: new Date(String(value)).toLocaleDateString('en-CA'),
                                field_type: filterInfo[newKey]?.field_type,
                                id: filterInfo[newKey]?.id,
                                is_field: true
                            });
                        }
                    } else if (key.endsWith("_end_date") && filter.appendedEnd) {
                        let newKey = key.replace("_end_date", "")
                        if (value) {
                            reqObj.push({
                                key: newKey,
                                operator: "LessEq",
                                value: new Date(String(value)).toLocaleDateString('en-CA'),
                                field_type: filterInfo[newKey]?.field_type,
                                id: filterInfo[newKey]?.id,
                                is_field: true
                            });
                        }
                    } else {
                        let newKey = key
                        if (value && (value as ValueType).value) {
                            reqObj.push({
                                key,
                                operator: "Eq",
                                value: (value as ValueType).value,
                                field_type: filterInfo[newKey]?.field_type,
                                id: filterInfo[newKey]?.id,
                                is_field: true
                            });
                        }
                    }
                }
            });
            const startDate = customFilters?.startCreatedDate;
            const endDate = customFilters?.endCreatedDate;
            console.log(startDate)
            console.log(endDate)
            console.log(createdDatesError)
            if ((startDate && !endDate) || (!startDate && endDate)) {
                setLoading(false);
                setCreatedDatesError(true);
                showToaster(toasterTypes.ERROR, "Select both Start and End Created Dates");
                return;
            }

            setShowFilterModal(false)

            if (startDate && endDate) {
                reqObj.push({
                    key: "created_date",
                    operator: "Between",
                    value: "",
                    field_type: "dateField",
                    id: "",
                    is_field: false,
                    start_created_at: startDate ? new Date(String(startDate)) : null,
                    end_created_at: endDate ? new Date(String(endDate)) : null
                });
            }

            let statsRespose;
            if (groupData[0]?.id === karnatakaBrainHealth.projectId && groupData[0]?.topicid === karnatakaBrainHealth.topicId) {
                statsRespose = await getFormStatskabhi(selectedForm?.id, reqObj);
            } else {
                statsRespose = await getFormStats(selectedForm?.id, reqObj);
            }
            if (!statsRespose?.data || statsRespose?.data.length === 0) {
                setNoStats(true);
                setLoading(false);
                return;
            }
            let transformedData: any = [];
            if (groupData[0]?.id === karnatakaBrainHealth.projectId && groupData[0]?.topicid === karnatakaBrainHealth.topicId) {
                transformedData = transformDataKabhi(statsRespose?.data);
            } else {
                transformedData = transformData(statsRespose?.data);
            }
            setSelectedFormData(transformedData);
            setNoStats(false);
        } catch (error) {
            console.error("Error fetching form data:", error);
        } finally {
            setLoading(false);
        }

    }

    function isValidDate(date) {
        const parsedDate = new Date(date);
        return !isNaN(parsedDate.getTime());
    }

    const createElementsFromFilters = (filters, filterInfo) => {
        const filterElements = {};
        Object.keys(filters).forEach((filterKey) => {
            const fieldType = filterInfo[filterKey]?.field_type;

            if (fieldType === "dateField") {
                let newFilterStartDateKey = filterKey + "_start_date";
                filterElements[newFilterStartDateKey] = {
                    name: newFilterStartDateKey,
                    placeholder: `Select ${filterKey}`,
                    label: filterKey + " start date",
                    type: FIELD_TYPES.DATE_TIME_PICKER,
                    appendedStart: true
                };

                let newFilterEndDateKey = filterKey + "_end_date";
                filterElements[newFilterEndDateKey] = {
                    name: newFilterEndDateKey,
                    placeholder: `Select ${filterKey}`,
                    label: filterKey + " end date",
                    type: FIELD_TYPES.DATE_TIME_PICKER,
                    appendedEnd: true
                };
            } else {
                filterElements[filterKey] = {
                    name: filterKey,
                    placeholder: `Select ${filterKey}`,
                    label: filterKey,
                    type: FIELD_TYPES.DROP_DOWN,
                    values: [
                        { value: null, label: "None" },
                        ...Object.keys(filters[filterKey]).map((value) => ({
                            value: value.trim(),
                            label: value.trim(),
                        })),
                    ],
                };
            }
        });

        return filterElements;
    };


    const saveLayoutToBackend = async (layout) => {
        try {
            const saveResponse = await saveDashConfig(selectedForm?.id, layout);
        } catch (error) {
            console.error('Failed to save layout:', error);
        }
    };

    const OnProjectDetail = () => {
        localStorage.setItem('projectData', JSON.stringify(projectData || {}))
        navigate("/projectdetail", { state: projectData || {} })
    }

    const toggleSort = () => {
        setSortBy(prev =>
            prev === 'none' ? 'ascending' : prev === 'ascending' ? 'descending' : 'none'
        );
    };

    const clearFilters = () => {
        reset();
        setCustomFilters({
            dateScale: { value: 'month', label: 'Month' },
            startCreatedDate: null,
            endCreatedDate: null
        })
    }

    return (
        <div className="main-projects-dashboard">
            <div className="heading-container">
                <div className="heading-div">
                    <ArrowLeftOutlined style={{ color: "#fff", fontSize: "36px", margin: "0px 10px" }} onClick={OnProjectDetail} />
                    <h1 className="heading">Dashboard</h1>
                </div>
                <div className="filter-button-container">
                    <Button className='filter-button' size="sm" onClick={() => setShowFilterModal(true)}>
                        <span>Filters</span><i className="icon icon-filter"></i>
                    </Button>
                    <Button className="sort-toggle-btn" onClick={toggleSort}>
                        {sortBy === 'descending' && <SortDescendingOutlined />}
                        {sortBy === 'ascending' && <SortAscendingOutlined />}
                        {sortBy === 'none' && <UnorderedListOutlined />}
                        {!sortBy && <UnorderedListOutlined />} {/* Default icon */}
                    </Button>

                </div>

            </div>
            <Row className="form-dropdown">
                <Col md={3}>
                    <FieldItem
                        {...elements.formDropDownValues}
                        value={formSelectedValue}
                        values={formsDropdownOptions}
                        onChange={(...e) => {
                            changeForms(...e);
                        }}
                        isDisabled={loading}
                    />
                </Col>
            </Row>
            {showFilterModal &&
                <ModalComponent
                    width={window.innerWidth > 700 ? window.innerWidth / 2 : window.innerWidth}
                    isOpen={true}
                    onClose={() => setShowFilterModal(false)}
                    header={"Filters"}
                    centered={false}
                    customClass="project-dashboard-filters"
                    body={
                        <>
                            <Row>
                                <Col md={6}>
                                    <FieldItem
                                        {...elements.dateScale}
                                        value={customFilters.dateScale}
                                        onChange={(...e) =>
                                            setCustomFilters(prev => ({
                                                ...prev,
                                                dateScale: e[0]
                                            }))
                                        }
                                        isDisabled={loading || !selectedForm || noStats}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FieldItem
                                        {...elements.startCreatedDate}
                                        value={customFilters.startCreatedDate}
                                        onChange={(...e) =>
                                            setCustomFilters(prev => ({
                                                ...prev,
                                                startCreatedDate: e[0]
                                            }))
                                        }
                                        isDisabled={loading || !selectedForm || noStats}
                                        error={createdDatesError}
                                        touched={createdDatesError}
                                    />
                                </Col>
                                <Col md={6}>
                                    <FieldItem
                                        {...elements.endCreatedDate}
                                        value={customFilters.endCreatedDate}
                                        onChange={(...e) =>
                                            setCustomFilters(prev => ({
                                                ...prev,
                                                endCreatedDate: e[0]
                                            }))
                                        }
                                        error={createdDatesError}
                                        touched={createdDatesError}
                                        isDisabled={loading || !selectedForm || noStats}
                                    />
                                </Col>
                            </Row>
                            <Row></Row>
                            {filters && (
                                <>
                                    {Object.keys(filters).reduce((rows: any, filterKey, index) => {
                                        const lastRow: any = rows[rows.length - 1];
                                        if (!lastRow || lastRow.length === 2) {
                                            rows.push([filterKey]);
                                        } else {
                                            lastRow.push(filterKey);
                                        }
                                        return rows;
                                    }, []).map((row, rowIndex) => (
                                        <Row key={rowIndex}>
                                            {row.map((filterKey) => (
                                                <Col md={6} key={filterKey}>
                                                    <FieldItem
                                                        {...filters[filterKey]}
                                                        value={values[filterKey]}
                                                        onChange={(...e) => {
                                                            handleChange(filters[filterKey].name, ...e);
                                                        }}
                                                        isDisabled={loading || !selectedForm || noStats}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    ))}
                                </>
                            )}

                        </>
                    }
                    footer={
                        <>
                            <CustomButton isDisabled={loading || !selectedForm || noStats} text="Clear" type="alert-primary" onClick={() => clearFilters()}></CustomButton>
                            <CustomButton isDisabled={loading || !selectedForm || noStats} text="Apply" type="primary" onClick={() => updateFilteredData()}></CustomButton>
                        </>
                    }
                />
            }
            {!loading && selectedFormData && (
                <DashboardCharts
                    data={selectedFormData}
                    initialLayout={initialDashConfig}
                    saveLayoutToBackend={saveLayoutToBackend}
                    sortBy={sortBy}
                    setSelectedFormData={setSelectedFormData}
                />
            )}
            {noStats && <div>No stats are configured for this form</div>}
            {loading ? (
                <div className="project-dashboard-loader">
                    <Loader loaderText="Loading" />
                </div>
            ) : ""}
        </div>
    );
};

export default ProjectDashboard;